import React, { useEffect, useRef, useState } from 'react';
import { adminFetch, rupiah } from './api';
import { ErrorBox, btnPrimary, btnSecondary } from './ui';
import BottomSheet from '../components/BottomSheet';
import { ProductPicker, summarizeSelection } from './ProductPicker';

// "Ubah produk" pesanan yang belum dibayar (PUT /api/admin/orders/{id}/items): SATU BottomSheet dua langkah —
// (1) pemilih produk BERSAMA (sama dengan halaman Buat pesanan) terisi item pesanan sekarang, (2) konfirmasi
// lama -> baru per produk + subtotal/total lama -> baru (perkiraan klien; total final dihitung server).

// Baris pesanan -> nilai pemilih (harga produk terkini dimuat terpisah; sementara pakai harga di pesanan).
export const linesFromOrder = (order) =>
  (order?.items || [])
    .filter((it) => it.productId)
    .map((it) => ({ productId: it.productId, qty: it.qty, name: it.name, unit: it.unit, fallbackUnitPrice: it.unitPrice }));

// Perbandingan lama -> baru per produk: added / removed / changed (qty) / same.
export const itemsDiff = (oldItems, next) => {
  const before = new Map((oldItems || []).filter((it) => it.productId).map((it) => [it.productId, it]));
  const after = new Map((next || []).map((l) => [l.productId, l]));
  const rows = [];
  (oldItems || []).forEach((it) => {
    const n = it.productId ? after.get(it.productId) : null;
    if (!n) rows.push({ kind: 'removed', name: it.name, from: it.qty, to: 0 });
    else if (n.qty !== it.qty) rows.push({ kind: 'changed', name: it.name, from: it.qty, to: n.qty });
    else rows.push({ kind: 'same', name: it.name, from: it.qty, to: n.qty });
  });
  (next || []).forEach((l) => {
    if (!before.has(l.productId)) rows.push({ kind: 'added', name: l.name, from: 0, to: l.qty });
  });
  return rows;
};

export const hasItemChanges = (oldItems, next) => itemsDiff(oldItems, next).some((r) => r.kind !== 'same');

const KIND = {
  added: { label: 'ditambah', cls: 'text-green-700' },
  removed: { label: 'dihapus', cls: 'text-red-700' },
  changed: { label: 'jumlah berubah', cls: 'text-amber-800' },
  same: { label: 'tetap', cls: 'text-gray-500' },
};

export const EditItemsSheet = ({ order, onClose, onSaved }) => {
  const [step, setStep] = useState('pick');
  const [lines, setLines] = useState(() => linesFromOrder(order));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const savingRef = useRef(false);

  // Harga & jenjang TERKINI produk yang sudah ada di pesanan (untuk total sementara); produk yang tidak ada /
  // nonaktif ditandai agar admin menghapus centangnya (server juga akan menolak).
  useEffect(() => {
    let alive = true;
    linesFromOrder(order).forEach((l) => {
      adminFetch(`/products/${l.productId}`)
        .then((p) => {
          if (!alive) return;
          setLines((cur) =>
            cur.map((x) =>
              x.productId === l.productId ? { ...x, name: p.name, unit: p.unit, price: p.price, tiers: p.tiers || [], unavailable: p.isActive === false } : x,
            ),
          );
        })
        .catch((err) => {
          if (alive && err?.status === 404) setLines((cur) => cur.map((x) => (x.productId === l.productId ? { ...x, unavailable: true } : x)));
        });
    });
    return () => {
      alive = false;
    };
  }, [order]);

  const changed = hasItemChanges(order.items, lines);
  const valid = lines.length > 0 && lines.every((l) => !l.unavailable);
  const sum = summarizeSelection(lines);
  const newTotal = sum.subtotal - (order.discount || 0) + (order.shippingFee || 0);
  const discountTooBig = (order.discount || 0) > sum.subtotal;
  const notify = order.customer?.accountType === 'registered';

  const close = () => {
    if (!savingRef.current) onClose();
  };
  const save = async () => {
    if (savingRef.current) return; // cegah klik ganda
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const res = await adminFetch(`/orders/${order.id}/items`, {
        method: 'PUT',
        body: { items: lines.map((l) => ({ productId: l.productId, qty: l.qty })) },
      });
      savingRef.current = false;
      onSaved(res);
    } catch (err) {
      savingRef.current = false;
      setSaving(false);
      setError(err);
    }
  };

  const header = (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 id="edit-items-title" className="text-lg font-bold text-gray-900">
          {step === 'pick' ? 'Ubah produk' : 'Simpan perubahan produk?'}
        </h2>
        <p className="text-sm text-gray-500">Pesanan {order.orderNo}</p>
      </div>
      <button type="button" onClick={close} aria-label="Tutup" className="-mr-1 -mt-1 flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100">
        ×
      </button>
    </div>
  );
  const footer =
    step === 'pick' ? (
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 text-sm">
          <div className="text-gray-600">
            {sum.lines} produk · {sum.items} item
          </div>
          <div className="font-bold text-purple-700" data-testid="edit-items-subtotal">
            {rupiah(sum.subtotal)} <span className="text-xs font-normal text-gray-500">sementara</span>
          </div>
        </div>
        <button type="button" className={btnPrimary} disabled={!changed || !valid} onClick={() => setStep('confirm')} data-testid="edit-items-next">
          Lanjut
        </button>
      </div>
    ) : (
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" className={btnSecondary} onClick={() => setStep('pick')} disabled={saving}>
          Kembali
        </button>
        <button type="button" className={btnPrimary} onClick={save} disabled={saving || !changed || !valid} data-testid="edit-items-save">
          {saving ? 'Menyimpan...' : 'Ya, simpan perubahan'}
        </button>
      </div>
    );

  return (
    <BottomSheet open onClose={close} labelledBy="edit-items-title" testId="edit-items" header={header} footer={footer}>
      {step === 'pick' ? (
        <div className="pb-2">
          {!changed && <p className="mb-2 text-xs text-gray-500">Belum ada perubahan.</p>}
          <ProductPicker value={lines} onChange={setLines} />
        </div>
      ) : (
        <div className="space-y-3 pb-2 text-sm" data-testid="edit-items-confirm">
          <ul className="space-y-1" data-testid="edit-items-diff">
            {itemsDiff(order.items, lines).map((r, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span className="min-w-0 [overflow-wrap:anywhere]">{r.name}</span>
                <span className={`shrink-0 font-semibold ${KIND[r.kind].cls}`}>
                  {r.kind === 'same' ? `× ${r.to}` : `${r.from} → ${r.to}`} <span className="text-xs font-normal">({KIND[r.kind].label})</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="space-y-1 border-t pt-2">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span data-testid="edit-items-subtotal-change">
                {rupiah(order.subtotal)} → {rupiah(sum.subtotal)}
              </span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Diskon (tetap)</span>
                <span>-{rupiah(order.discount)}</span>
              </div>
            )}
            {order.shippingFee > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Ongkir (tetap)</span>
                <span>{rupiah(order.shippingFee)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-purple-700">
              <span>Total</span>
              <span data-testid="edit-items-total-change">
                {rupiah(order.total)} → {rupiah(newTotal)}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500">Harga dihitung ulang dengan harga terbaru; total final dihitung server.</p>
          {discountTooBig && <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-900">Diskon pesanan melebihi subtotal baru; ubah diskon terlebih dahulu.</p>}
          {notify && <p className="rounded-md bg-blue-50 px-3 py-2 text-blue-900" data-testid="edit-items-notify">Pelanggan akan menerima notifikasi.</p>}
          {error && <ErrorBox error={error} />}
        </div>
      )}
    </BottomSheet>
  );
};

export default EditItemsSheet;
